import type {AnomalyWithReasonInterface} from '@shared/interfaces/anomaly'
import {writeSignalStyles} from '@containers/SignalsWorkspace/signalStyle'

// "Go to anomaly" navigation (AB#5931 phase 2). The old link targeted the
// classic signals page's anomalies tab; the explorer reshape dropped the
// batch/anomaly params, so it landed on an unrelated favourites view. The
// magnifier now goes STRAIGHT to the Signal Explorer in overlay mode: the
// anomaly's program overlaid with exactly its batch hand-picked
// (rule=selection + bi — window-independent, the by-ids recovery resolves the
// batch even outside the time window) and its signal plotted. If the program
// has an active anomaly detection model, the model band shows alongside.

const MS_PER_MINUTE = 60_000
const MIN_BUFFER_MS = 5 * MS_PER_MINUTE

// The time window only matters if the user toggles overlay OFF — frame the
// anomaly with the legacy 20% buffer so that view lands right too.
const bufferedWindow = (startMs: number, endMs: number): [Date, Date] => {
  const bufferMs = Math.max((endMs - startMs) * 0.2, MIN_BUFFER_MS)
  return [new Date(startMs - bufferMs), new Date(endMs + bufferMs)]
}

export const buildAnomalyExplorerUrl = (
  anomaly: AnomalyWithReasonInterface,
  nowMs: number,
): string => {
  const params = new URLSearchParams()
  if (anomaly.signal?.id) {
    params.set('sig', anomaly.signal.id)
    // Anomaly shades are opt-in per signal (AB#6763) -- switch them on here,
    // the user came to see this one.
    writeSignalStyles(params, {[anomaly.signal.id]: {anomalies: true}})
  }
  // Hierarchy filter context (rail scoping + the program picker's equipment
  // gate) — same params the explorer reads (lineId/equipmentId).
  if (anomaly.machine?.id) {
    params.set('lineId', anomaly.machine.id)
  }
  if (anomaly.machine_sub_system?.id) {
    params.set('equipmentId', anomaly.machine_sub_system.id)
  }
  if (anomaly.program?.id) {
    params.set('prog', anomaly.program.id)
    params.set('overlay', '1')
    params.set('rule', 'selection')
    if (anomaly.batch?.id) {
      params.set('bi', anomaly.batch.id)
    }
  }
  const startMs = anomaly.start ? new Date(anomaly.start).getTime() : null
  if (startMs !== null && !Number.isNaN(startMs)) {
    const endMs = anomaly.end ? new Date(anomaly.end).getTime() : nowMs
    const [start, end] = bufferedWindow(startMs, Math.max(endMs, startMs + MS_PER_MINUTE))
    params.set('start_date', start.toISOString())
    params.set('end_date', end.toISOString())
    params.set('time_range', 'null')
  }
  return `/signal-explorer?${params.toString()}`
}
